import React, { useState } from 'react'
import styles from './lesson-overview-table.module.css'
import { useGradesData, StudentRow, GradeCell } from './lesson-overview-context'
import { SlotPercent } from 'util/grades-grid'

export type ViewMode = 'grid' | 'list'

const TIER_CLASS: Record<string, string> = {
	Emerging: styles.tEmerging,
	Intermediate: styles.tIntermediate,
	Advanced: styles.tAdvanced,
}

const TierTag = ({ tier }: { tier: string }) => (
	<span className={`${styles.tierTag} ${TIER_CLASS[tier] || ''}`}>
		<span className={styles.tierDot} />
		{tier}
	</span>
)

type GradeTarget = { row: StudentRow; slotIndex: number }

export const GradesTable = ({ view, rows, slotPercents }: { view: ViewMode; rows: StudentRow[]; slotPercents: SlotPercent[] }) => {
	const { slots, gradeCell } = useGradesData()
	const [target, setTarget] = useState<GradeTarget | null>(null)

	if (!rows.length) {
		return <div className={styles.card}><p className={styles.empty}>No students at this level.</p></div>
	}

	const lowestPct = Math.min(...slotPercents.filter(s => s.pct !== null).map(s => s.pct as number))

	const openCell = (row: StudentRow, slotIndex: number) => {
		const cell = row.cells[slotIndex]
		if (cell.state !== 'correct' && cell.state !== 'incorrect') return
		setTarget({ row, slotIndex })
	}

	return (
		<div className={styles.card}>
			<div className={styles.scroll}>
				<table>
					{view === 'list' ? (
						<>
							<thead>
								<tr>
									<th>Student</th>
									<th>Level</th>
									<th>Questions graded</th>
									<th>Overall</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, i) => (
									<tr key={row.studentId}>
										<td className={styles.who}>
											<div className={styles.whoCell}><span className={styles.rn}>{i + 1}</span><span className={styles.nm}>{row.studentName}</span></div>
										</td>
										<td><TierTag tier={row.tier} /></td>
										<td>{row.gradedCount} / {slots.length} graded</td>
										<td className={styles.score}>
											{row.overallPct === null ? '—' : `${row.overallPct}%`}
											{row.gradedCount < slots.length && <span className={styles.partial}>of {row.gradedCount} graded</span>}
										</td>
									</tr>
								))}
							</tbody>
						</>
					) : (
						<>
							<thead>
								<tr>
									<th>Student</th>
									<th>Level</th>
									{slots.map((slot, i) => {
										const pct = slotPercents[i]?.pct
										const hard = pct !== null && pct === lowestPct && pct < 100
										return (
											<th key={slot.slotKey} className={`${styles.qcol} ${hard ? styles.qcolHard : ''}`}>
												<span className={styles.qnum}>Q{i + 1}</span>
												<span className={styles.qpct}>{pct === null || pct === undefined ? '—' : `${pct}%`}</span>
											</th>
										)
									})}
									<th style={{ textAlign: 'right' }}>Overall</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, i) => (
									<tr key={row.studentId}>
										<td className={styles.who}>
											<div className={styles.whoCell}><span className={styles.rn}>{i + 1}</span><span className={styles.nm}>{row.studentName}</span></div>
										</td>
										<td><TierTag tier={row.tier} /></td>
										{row.cells.map((cell, slotIndex) => (
											<td key={slotIndex} className={styles.cell}>
												<GradeMark cell={cell} onClick={() => openCell(row, slotIndex)} label={`Q${slotIndex + 1}`} />
											</td>
										))}
										<td className={styles.score} style={{ textAlign: 'right' }}>
											{row.overallPct === null ? '—' : `${row.overallPct}%`}
											{row.gradedCount < slots.length && <span className={styles.partial}>of {row.gradedCount} graded</span>}
										</td>
									</tr>
								))}
							</tbody>
						</>
					)}
				</table>
			</div>
			{view === 'grid' && (
				<div className={styles.legend}>
					<span><span className={`${styles.mark} ${styles.ok}`}>✓</span> Correct</span>
					<span><span className={`${styles.mark} ${styles.no}`}>✕</span> Incorrect</span>
					<span><span className={`${styles.mark} ${styles.ungraded}`}>–</span> No submission yet</span>
					<span className={styles.legendNote}>Column % = how much of the class got that question right</span>
				</div>
			)}
			{target && (
				<GradeModal
					target={target}
					onClose={() => setTarget(null)}
					onMark={correct => {
						gradeCell(target.row.studentId, target.slotIndex, correct)
						setTarget(null)
					}}
				/>
			)}
		</div>
	)
}

const GradeMark = ({ cell, onClick, label }: { cell: GradeCell; onClick: () => void; label: string }) => {
	if (cell.state === 'correct') {
		return <button type="button" className={`${styles.mark} ${styles.ok}`} title={`${label}: correct — click to review`} onClick={onClick}>✓</button>
	}
	if (cell.state === 'incorrect') {
		return <button type="button" className={`${styles.mark} ${styles.no}`} title={`${label}: incorrect — click to review`} onClick={onClick}>✕</button>
	}
	const title = cell.state === 'not-submitted' ? `${label}: not yet submitted` : `${label}: not part of this student's lesson`
	return <span className={`${styles.mark} ${styles.ungraded}`} title={title} aria-label={title}>–</span>
}

const GradeModal = ({ target, onClose, onMark }: { target: GradeTarget; onClose: () => void; onMark: (correct: boolean) => void }) => {
	const cell = target.row.cells[target.slotIndex]
	const isCorrect = cell.state === 'correct'

	React.useEffect(() => {
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [onClose])

	return (
		<div className={styles.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
			<div className={styles.panel} role="dialog" aria-modal="true">
				<div className={styles.panelHead}>
					<div>
						<h2>Question {target.slotIndex + 1}</h2>
						<div className={styles.who2}>{target.row.studentName} · {target.row.tier}</div>
					</div>
					<button type="button" className={styles.iconBtn} aria-label="Close" onClick={onClose}>
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
					</button>
				</div>
				<div className={styles.panelBody}>
					<div className={styles.qLabel}>The question</div>
					<p className={styles.qBody}>{cell.questionText}</p>
					<div className={`${styles.opt} ${styles.optAnswer}`}>
						<span className={styles.letter}>A</span>
						{cell.studentAnswer}
						<span className={`${styles.flag} ${isCorrect ? styles.flagOk : styles.flagPicked}`}>Their answer{isCorrect ? ' · correct' : ''}</span>
					</div>
					{!isCorrect && cell.correctAnswer && (
						<div className={`${styles.opt} ${styles.optCorrect}`}>
							<span className={styles.letter}>B</span>
							{cell.correctAnswer}
							<span className={`${styles.flag} ${styles.flagOk}`}>Correct answer</span>
						</div>
					)}
				</div>
				<div className={styles.panelFoot}>
					<button type="button" className={`${styles.btnMark} ${styles.right} ${isCorrect ? styles.on : ''}`} onClick={() => onMark(true)}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
						Correct
					</button>
					<button type="button" className={`${styles.btnMark} ${styles.wrong} ${!isCorrect ? styles.on : ''}`} onClick={() => onMark(false)}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
						Incorrect
					</button>
					{cell.manuallyGraded && <span className={styles.panelNav}>Manually graded</span>}
				</div>
			</div>
		</div>
	)
}
